import { gql } from '@apollo/client';

/**
 * Used by Experiments Team
 */
export const fetchCampaignsQuery = gql`
  fragment campaignsPageInfo on Campaigns {
    info {
      count
      limit
      offset
      page
    }
  }
  query getCampaigns(
    $filter: CampaignFilterInfoInput
    $pagination: Pagination
    $search: String
    $sort: Sort
  ) {
    campaigns(campaignFilterInfo: $filter, pagination: $pagination, search: $search, sort: $sort) {
      ...campaignsPageInfo
      results {
        id
        name
        campaignTypeDefinition
        templateId
        messageMedium
        startAt
        endedAt
        createdAt
        updatedAt
        updatedBy
        labels {
          id
          name
        }
        campaignState
        createdBy
        lists {
          id
          name
        }
        suppressionLists {
          id
          name
        }
        schedulerMsgParams {
          recurDays
          endDate
        }
        experiments {
          id
          experimentType
          name
          state
        }
        dataScienceOptimizations
        campaignAlertsEnabled
        mobileAppIds
        priorityLevel
        workflow {
          id
          name
        }
        aiCreation
      }
    }
  }
`;

export const fetchCampaignCreatorsQuery = gql`
  query getCampaignCreators {
    campaignCreators {
      id
      fullName
      avatarUrl
    }
  }
`;

export const fetchCampaignModifiersQuery = gql`
  query getCampaignModifiers {
    campaignModifiers {
      id
      fullName
      avatarUrl
    }
  }
`;

export const fetchCampaignLabelsQuery = gql`
  query getCampaignLabels {
    campaignLabels {
      id
      name
    }
  }
`;

export const fetchCampaignStatesQuery = gql`
  query getCampaignStates {
    campaignStates
  }
`;

export const fetchCampaignTypesQuery = gql`
  query getCampaignTypes {
    campaignTypes
  }
`;

type Project = {
  id: number;
  groupRecurringCampaigns?: boolean;
};

export type FetchCampaignProjectResponse = {
  currentProject: Project;
};

export const fetchCampaignProject = gql`
  query getCurrentProject {
    currentProject {
      id
      groupRecurringCampaigns
    }
  }
`;

const campaignResponseFragment = `
  fragment campaignListInfo on Campaign {
    campaignAlertsEnabled
    campaignState
    campaignTypeDefinition
    createdAt
    createdBy
    customQuietHours {
      startTime
      endTime
      sendSkip
    }
    dataScienceOptimizations
    aiCreation
    endedAt
    experiments {
      id
      experimentType
      name
      state
    }
    id
    labels {
      id
      name
    }
    lists {
      id
      name
    }
    messageMedium
    mobileAppIds
    name
    placementId
    priorityLevel
    schedulerMsgParams {
      recurDays
      endDate
      recurSchedule {
        period
        adjustments {
          ... on DayOfMonth {
           day
           type:__typename
          }
        }
      }
    }
    startAt
    suppressionLists {
      id
      name
    }
    templateContentPreview
    templateId
    templateType
    updatedAt
    updatedBy
    workflow {
      id
      name
    }
  }`;

export const fetchCampaignFolderQuery = gql`
  ${campaignResponseFragment}

  query fetchFolderQuery(
    $content: [ContentFilter!]
    $campaignFilterInfo: CampaignFilterInfoInput
    $folderId: Long
    $pagination: Pagination
    $recursive: Boolean
    $search: String
    $sort: Sort
  ) {
    campaignFolder(
      content: $content
      campaignFilterInfo: $campaignFilterInfo
      folderId: $folderId
      pagination: $pagination
      recursive: $recursive
      search: $search
      sort: $sort
    ) {
      info {
        count
        offset
        limit
        page
      }
      ancestors {
        id
        name
      }
      content {
        ... on CampaignSubfolder {
          id
          name
        }
        ... on Campaign {
          id
          ...campaignListInfo
        }
      }
      id
      name
    }
  }
`;

export const fetchParentCampaign = gql`
  ${campaignResponseFragment}
  query fetchCampaign($campaignId: Long!) {
    campaign(id: $campaignId) {
      ...campaignListInfo
    }
  }
`;
